import { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Flame, Trophy, Diamond, Star, Eye, EyeOff, ChevronRight } from 'lucide-react';
import Loader2 from '@/components/brand/Loader2';
import WeightDisc from '@/components/brand/WeightDisc';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Badge icon mapping
const badgeIcons = {
  'STREAK_3': { icon: Flame, color: 'text-orange-400', bg: 'from-orange-500/20 to-red-500/20' },
  'STREAK_7': { icon: Flame, color: 'text-orange-400', bg: 'from-orange-500/20 to-red-500/20' },
  'STREAK_14': { icon: Diamond, color: 'text-primary', bg: 'from-primary/20 to-blue-500/20' },
  'STREAK_30': { icon: Diamond, color: 'text-primary', bg: 'from-primary/20 to-blue-500/20' },
  'STREAK_60': { icon: Star, color: 'text-amber-400', bg: 'from-amber-500/20 to-yellow-500/20' },
  'STREAK_90': { icon: Trophy, color: 'text-amber-400', bg: 'from-amber-500/20 to-yellow-500/20' },
};

const StreakCard = ({ compact = false }) => {
  const [loading, setLoading] = useState(true);
  const [streak, setStreak] = useState(null);
  const [badges, setBadges] = useState([]);
  const [nextBadge, setNextBadge] = useState(null);
  const [preferences, setPreferences] = useState({ streak_visible: true, badges_visible: true });
  const [showAnimation, setShowAnimation] = useState(false);

  useEffect(() => {
    loadStreakData();
  }, []);

  const loadStreakData = async () => {
    try {
      const [streakRes, badgesRes, prefsRes] = await Promise.all([
        axios.get(`${API}/streaks`),
        axios.get(`${API}/streaks/badges`),
        axios.get(`${API}/streaks/preferences`)
      ]);
      
      setStreak(streakRes.data);
      setBadges(badgesRes.data.badges || []);
      setNextBadge(badgesRes.data.next_badge);
      setPreferences(prefsRes.data);
    } catch (error) {
      console.error('Error loading streak:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleVisibility = async () => {
    try {
      const newVisible = !preferences.streak_visible;
      await axios.patch(`${API}/streaks/preferences`, {
        streak_visible: newVisible
      });
      setPreferences(prev => ({ ...prev, streak_visible: newVisible }));
      toast.success(newVisible ? 'Racha visible' : 'Racha oculta');
    } catch (error) {
      toast.error('Error al actualizar preferencias');
    }
  };

  if (loading) {
    return (
      <Card className="card-default">
        <CardContent className="p-4 flex items-center justify-center">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (!preferences.streak_visible) {
    return (
      <Card className="card-default">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-zinc-500">
              <EyeOff className="w-5 h-5" />
              <span className="text-sm">Racha oculta</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleVisibility}
              className="text-zinc-400 hover:text-white text-xs"
            >
              Mostrar
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentCount = streak?.current_count || 0;
  const bestCount = streak?.best_count || 0;
  const status = streak?.status || 'active';
  const daysUntilReset = streak?.days_until_reset || 3;

  // Determine display based on status
  let statusColor = 'text-orange-400';
  let statusBg = 'from-orange-500/20 to-red-500/20';
  let statusMessage = '';

  if (status === 'paused') {
    statusColor = 'text-amber-400';
    statusBg = 'from-amber-500/20 to-yellow-500/20';
    statusMessage = daysUntilReset === 1 
      ? '¡Entrena hoy para mantener tu racha!'
      : `${daysUntilReset} días para retomar`;
  } else if (currentCount >= 30) {
    statusColor = 'text-primary';
    statusBg = 'from-primary/20 to-purple-500/20';
  }

  // Compact version for sidebar or small widgets
  if (compact) {
    return (
      <div className="flex items-center gap-3 p-3 bg-zinc-900 rounded-sm">
        <div className={`w-10 h-10 rounded-sm bg-gradient-to-br ${statusBg} flex items-center justify-center`}>
          <Flame className={`w-5 h-5 ${statusColor}`} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className={`text-2xl font-bold font-teko ${statusColor}`}>{currentCount}</span>
            <span className="text-zinc-500 text-xs">días</span>
          </div>
          {status === 'paused' && (
            <span className="text-amber-400 text-xs">{statusMessage}</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <Card className="card-default overflow-hidden">
      <CardContent className="p-0">
        {/* Header with streak count */}
        <div className={`p-4 sm:p-6 bg-gradient-to-br ${statusBg}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className={`w-14 h-14 sm:w-16 sm:h-16 shrink-0 rounded-sm bg-zinc-950/50 flex items-center justify-center ${showAnimation ? 'animate-bounce-in' : ''}`}>
                <WeightDisc size={44} label="" className="[animation-duration:40s]" />
              </div>
              <div className="min-w-0">
                <div className="flex items-baseline gap-2 whitespace-nowrap">
                  <span className={`text-4xl sm:text-5xl font-bold font-teko leading-none ${statusColor}`}>
                    {currentCount}
                  </span>
                  <span className="text-zinc-400 text-sm">{currentCount === 1 ? 'día' : 'días'}</span>
                </div>
                <p className="text-zinc-400 text-sm mt-1">
                  {status === 'active' && currentCount > 0 && 'Racha activa'}
                  {status === 'active' && currentCount === 0 && 'Empieza hoy tu racha'}
                  {status === 'paused' && statusMessage}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleVisibility}
              className="text-zinc-400 hover:text-white"
              aria-label="Ocultar racha"
            >
              <Eye className="w-4 h-4" />
            </Button>
          </div>

          {/* Progress to next badge */}
          {nextBadge && Number.isFinite(nextBadge.days) && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="text-xs text-zinc-400 shrink-0">Próximo badge</span>
                <span className="text-xs text-zinc-300 text-right min-w-0 truncate">
                  {nextBadge.title}
                </span>
              </div>
              <div className="h-2 bg-zinc-950/50 rounded-full overflow-hidden">
                <div 
                  className={`h-full bg-gradient-to-r ${statusBg.replace('/20', '/60')} transition-all duration-500`}
                  style={{ 
                    width: `${Math.min(100, (currentCount / nextBadge.days) * 100)}%` 
                  }}
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1 text-right">
                {nextBadge.days - currentCount === 1 ? '1 día restante' : `${nextBadge.days - currentCount} días restantes`}
              </p>
            </div>
          )}
        </div>

        {/* Best record */}
        {bestCount > 0 && bestCount !== currentCount && (
          <div className="px-6 py-3 border-b border-zinc-800 flex items-center justify-between">
            <span className="text-xs text-zinc-500">Mejor racha</span>
            <div className="flex items-center gap-1">
              <Trophy className="w-4 h-4 text-amber-400" />
              <span className="text-zinc-300 text-sm font-medium">{bestCount} {bestCount === 1 ? 'día' : 'días'}</span>
            </div>
          </div>
        )}

        {/* Badges */}
        {preferences.badges_visible && badges.length > 0 && (
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-zinc-500 uppercase tracking-wider">Badges desbloqueados</span>
              <Link 
                to="/app/settings/streaks"
                className="text-xs text-primary hover:text-primary flex items-center gap-1"
              >
                Ver todos <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="flex flex-wrap gap-2">
              {badges.slice(0, 4).map((badge) => {
                const iconData = badgeIcons[badge.badge_code] || badgeIcons['STREAK_3'];
                const IconComponent = iconData.icon;
                
                return (
                  <div
                    key={badge.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-sm bg-gradient-to-br ${iconData.bg}`}
                    title={badge.description}
                  >
                    <WeightDisc size={18} label="" />
                    <span className="text-xs text-zinc-300">{badge.title}</span>
                  </div>
                );
              })}
              {badges.length > 4 && (
                <div className="flex items-center px-3 py-2 rounded-sm bg-zinc-800">
                  <span className="text-xs text-zinc-400">+{badges.length - 4} más</span>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StreakCard;
